// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { awPageGrey } from 'Utils/themes';
import { PAGE_TOOLBAR_HEIGHT_PX } from './PageToolbar/styles';

// The content column's side padding, in theme spacing units. The controls band that pins over
// the scrolling list bleeds across it, so the shadows of the cards passing beneath - which reach
// past the cards' boxes into this padding - are covered too.
export const CONTENT_COLUMN_SIDE_PADDING = 2;

const styles = (theme: any, showMapView: boolean) => {
  const mapViewHeadingSpacing = showMapView && {
    width: '80%',
    margin: '0 auto',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  };

  return {
    // The page's ground. The shell's content wrapper is the scroll container and a row flex
    // box, which would stretch this item to exactly one viewport and let the list overflow it
    // onto the body's white; sized to its content instead (and never shorter than the viewport)
    // the ground follows the list to its end (AUT-6509).
    contentWrapperGrid: {
      position: 'relative',
      flex: 1,
      // A flex item of the shell's row: without this it would take the widest row's unbroken width
      minWidth: 0,
      alignSelf: 'flex-start',
      minHeight: '100%',
      backgroundColor: awPageGrey,
    },
    contentWrapperMap: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      minWidth: '100%',
    },
    // Map view swaps the content area for the map: the toolbar keeps its row, the map takes the
    // rest, and its height comes from the flex column rather than a viewport calculation.
    mapviewContent: {
      display: 'flex',
      flex: '1 1 auto',
      minHeight: 0,
      flexDirection: 'column',
    },
    // The rail and the map share the map view's full height (Figma: no top toolbar here).
    mapviewBody: {
      display: 'flex',
      flexDirection: 'row',
      flex: '1 1 auto',
      minHeight: 0,
    },
    mapArea: {
      position: 'relative',
      flex: '1 1 auto',
      minHeight: 0,
    },
    mapEmptyState: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      textAlign: 'center',
      backgroundColor: theme.palette.common.white,
      padding: theme.spacing(4),
      borderRadius: '8px',
      boxShadow: theme.shadows[3],
      zIndex: 1,
    },
    // Projects excluded for having no resolvable location, surfaced rather than silently dropped.
    mapMissingLocation: {
      position: 'absolute',
      bottom: theme.spacing(3),
      left: theme.spacing(3),
      backgroundColor: theme.palette.common.white,
      color: theme.palette.darkGrey,
      padding: theme.spacing(1, 2),
      borderRadius: '16px',
      boxShadow: theme.shadows[2],
      fontSize: '13px',
      zIndex: 1,
    },
    gridList: {
      paddingBottom: theme.spacing(3),
      display: 'grid',
      // Three columns at the design's width, fewer as the container narrows, rather than
      // three columns squeezed to any width. 320px is the narrowest a card stays readable
      // at; at the 1200px content width this still resolves to the Figma's three.
      // The explicit minimum also keeps a card's unbreakable rows (its name and REF line
      // never wrap) from widening every track: a bare 1fr is minmax(auto, 1fr), and one long
      // name would push the whole grid past the page.
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gridGap: 30,
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(5),
    },
    // The design is drawn at 1200px; below that the content follows the viewport instead of
    // overflowing it.
    gridListWrapper: {
      width: '100%',
      maxWidth: '1200px',
      // Starts under the fixed toolbar, with the design's breathing room below it.
      margin: `${PAGE_TOOLBAR_HEIGHT_PX + 10}px auto`,
      paddingLeft: theme.spacing(CONTENT_COLUMN_SIDE_PADDING),
      paddingRight: theme.spacing(CONTENT_COLUMN_SIDE_PADDING),
      boxSizing: 'border-box',
    },
    dashboardContent: {
      display: 'flex',
      flex: '1 1 auto',
      position: 'relative',
      minHeight: 0,
      flexDirection: 'row',
    },
    mapviewLayoutLeft: {
      flexGrow: 1,
      width: '50%',
      flex: 1,
      // Narrower than a row's unbroken name, the column follows the screen and the name ellipsizes, rather than
      // the column taking the name's width and the page scrolling sideways.
      minWidth: 0,
      // The page scrolls in the shell's content wrapper, never here: an overflow of its own
      // would make this content-sized box the scroll container the controls band pins against,
      // and a box that never scrolls never pins anything.
      overflow: 'visible',
      margin: 0,
      position: 'relative',
      flexDirection: 'column',
    },
    gridItemWrapper: {
      overflow: 'inherit',
    },
    loading: {
      display: 'flex',
      justifyContent: 'center',
    },
    // The list view: rows stacked full-width, the window's spacers as siblings around it.
    rowList: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5),
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(5),
      paddingBottom: theme.spacing(3),
    },
    // The empty projects section keeps the content column's width, controls included.
    emptyProjects: {
      width: '100%',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    noProjects: {
      width: '100%',
      textAlign: 'center',
      marginTop: '20vh',
    },
    retryButton: {
      marginTop: theme.spacing(3),
    },
    listTitle: {
      fontSize: '18px',
      fontWeight: 600,
      ...mapViewHeadingSpacing,
    },
  };
};

export default styles;
